/**
 * ZenMarkup Privacy Analytics — Overview Devices widget.
 *
 * Icon + label + animated percentage bar per device type.
 * Extracted from overview-widgets.js to keep modules under 200 lines.
 */

import { setWidgetContent, emptyState } from './table-builder.js';

let hasAnimated = false;
const STAGGER = 150;

/**
 * Read device SVG icons from data attributes on the widget element.
 *
 * @return {Object} Map of device type → SVG markup.
 */
function getIcons() {
	const widget = document.querySelector('[data-el="overview-devices"]');
	if (!widget) return {};
	return {
		Desktop: widget.getAttribute('data-icon-desktop') || '',
		Mobile: widget.getAttribute('data-icon-mobile') || '',
		Tablet: widget.getAttribute('data-icon-tablet') || ''
	};
}

/**
 * Render devices card for Overview tab.
 *
 * @param {Object} data  Full stats response data.
 */
/**
 * Ensure all 3 device types are present, adding missing ones with count 0.
 *
 * @param {Array} devices  Raw devices array from API.
 * @return {Array} Array with Desktop, Mobile, Tablet guaranteed.
 */
function ensureAllTypes(devices) {
	const ALL_TYPES = ['Desktop', 'Mobile', 'Tablet'];
	const map = {};
	devices.forEach(function (d) { map[String(d.device_type).toLowerCase()] = d.visitors; });
	return ALL_TYPES.map(function (type) {
		return { device_type: type, count: map[type.toLowerCase()] || 0 };
	});
}

export function render(data) {
	const rawDevices = data.devices || [];
	const devices = ensureAllTypes(rawDevices);

	const total = devices.reduce(function (sum, d) { return sum + d.count; }, 0);

	if (total === 0) {
		setWidgetContent('overview-devices', emptyState('No device data yet.'));
		return;
	}

	const animate = !hasAnimated;
	const deviceBars = [];
	const icons = getIcons();

	const list = document.createElement('div');
	list.className = 'zmpa-device-list';

	devices.forEach(function (d, idx) {
		const pctNum = total > 0 ? (d.count / total) * 100 : 0;
		const pct = pctNum.toFixed(1);
		const targetWidth = Math.max(pctNum, 1) + '%';

		const row = document.createElement('div');
		row.className = 'zmpa-device-item';

		const iconWrap = document.createElement('span');
		iconWrap.className = 'zmpa-device-icon';
		iconWrap.innerHTML = icons[d.device_type] || icons.Desktop;

		const info = document.createElement('div');
		info.className = 'zmpa-device-info';

		const top = document.createElement('div');
		top.className = 'zmpa-device-top';

		const label = document.createElement('span');
		label.className = 'zmpa-device-label';
		label.textContent = d.device_type;

		const pctEl = document.createElement('span');
		pctEl.className = 'zmpa-device-pct';
		pctEl.textContent = pct + '%';

		top.appendChild(label);
		top.appendChild(pctEl);

		const barTrack = document.createElement('div');
		barTrack.className = 'zmpa-device-bar-track';

		const barFill = document.createElement('div');
		barFill.className = 'zmpa-device-bar-fill';

		if (animate) {
			barFill.style.width = '0%';
			deviceBars.push({ el: barFill, target: targetWidth, delay: idx * STAGGER });
		} else {
			barFill.style.width = targetWidth;
		}

		barTrack.appendChild(barFill);
		info.appendChild(top);
		info.appendChild(barTrack);

		row.appendChild(iconWrap);
		row.appendChild(info);
		list.appendChild(row);
	});

	setWidgetContent('overview-devices', list);

	if (animate && deviceBars.length) {
		hasAnimated = true;
		requestAnimationFrame(function () {
			deviceBars.forEach(function (b) {
				setTimeout(function () { b.el.style.width = b.target; }, b.delay);
			});
		});
	}
}
